import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Auth.css';

/**
 * Unified Auth Success Handler
 * 
 * This component handles authentication success for both:
 * - Google OAuth (redirected from backend)
 * - Manual login (if redirected here)
 * 
 * Flow:
 * 1. Store token in localStorage
 * 2. Backend has already merged guest cart (happens during OAuth/login)
 * 3. CartContext will automatically fetch merged cart when auth state changes
 * 4. Redirect to intended path (e.g., /cart)
 */
function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const error = searchParams.get('error');

  useEffect(() => {
    if (error) {
      navigate('/login?error=oauth_failed');
      return;
    }

    if (token) {
      // Step 1: Store token (same as manual login)
      localStorage.setItem('token', token);
      
      // Dispatch custom event to notify AuthContext of token change
      window.dispatchEvent(new CustomEvent('tokenStored', { detail: { token } }));

      // Step 2: Read intended redirect path (stored before OAuth started)
      // This was set in googleLogin() or could be from manual login redirect
      let redirectPath = '/';
      try {
        // Read redirect path from localStorage (stored before OAuth)
        const stored = localStorage.getItem('post_login_redirect');
        console.log('[AuthCallback] Reading redirect path from localStorage:', stored);
        console.log('[AuthCallback] All localStorage keys:', Object.keys(localStorage));
        
        if (stored && typeof stored === 'string' && stored.trim() !== '') {
          redirectPath = stored.trim();
          console.log('[AuthCallback] Using redirect path:', redirectPath);
          // Clean up the stored redirect path AFTER we've read it
          localStorage.removeItem('post_login_redirect');
          console.log('[AuthCallback] Cleaned up redirect path from localStorage');
        } else {
          console.warn('[AuthCallback] No valid redirect path found in localStorage, defaulting to home');
          console.log('[AuthCallback] Stored value was:', stored, 'Type:', typeof stored);
        }
      } catch (error) {
        console.error('[AuthCallback] Error reading redirect path:', error);
        // Fallback to home on storage error
        redirectPath = '/';
      }
      
      console.log('[AuthCallback] Final redirect path:', redirectPath);

      // Clear token from URL for security
      window.history.replaceState({}, document.title, '/auth/callback');

      // Step 3: Show welcome toast if redirecting to cart
      if (redirectPath === '/cart') {
        // Store flag to show toast after page loads
        sessionStorage.setItem('show_cart_welcome', 'true');
      }

      // Step 4: Redirect to intended path
      // CartContext will automatically fetch merged cart when auth state changes
      // (via useEffect that watches isAuthenticated)
      console.log('[AuthCallback] About to redirect to:', redirectPath);
      console.log('[AuthCallback] Current URL:', window.location.href);
      
      // Use a small delay to ensure all operations complete
      // Then redirect to the intended path
      setTimeout(() => {
        console.log('[AuthCallback] Executing redirect to:', redirectPath);
        window.location.href = redirectPath;
      }, 200);
    } else {
      // No token in URL - this shouldn't happen, but redirect to login
      navigate('/login?error=oauth_failed');
    }
  }, [token, error, navigate]);

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-loading">
          <div className="auth-spinner"></div>
          <p>Completing sign in...</p>
        </div>
      </div>
    </div>
  );
}

export default AuthCallback;
